/**
 * Typed HTTP client for arcadia84-api.
 *
 * Reads `VITE_API_BASE_URL` from the build-time env. Local dev defaults
 * to http://localhost:8084/v1 (port matches apps/api/.env). Each
 * fetch attaches a per-session `X-Request-ID` so the API log lines
 * can be correlated back to a cabinet session.
 *
 * Score submission gets a small retry budget (3 attempts with
 * exponential backoff) because the runtime calls it fire-and-forget at
 * round-end — a transient blip shouldn't lose the score, but we also
 * can't block the next round forever. Leaderboard reads are single-shot;
 * the player can re-open the pause menu to retry.
 */

import type {
  ClaimAnonymousScoresRequest,
  ClaimAnonymousScoresResponse,
  DailySeedResponse,
  GlobalPlays,
  Leaderboard,
  LeaderboardPeriod,
  PlayRecord,
  ScoreSubmission,
  ScoreSubmissionResponse,
} from '@arcraz/arcadia-types';
import { getAccessToken } from '../auth/session.js';

const SESSION_REQUEST_ID = newSessionId();
const SUBMIT_RETRIES = 3;
const SUBMIT_BACKOFF_BASE_MS = 250;

function newSessionId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function baseUrl(): string {
  // import.meta.env is Vite-injected at build time; VITE_API_BASE_URL is
  // picked up automatically without a vite.config define block.
  const fromEnv = (import.meta.env?.VITE_API_BASE_URL as string | undefined)?.trim();
  if (fromEnv && fromEnv.length > 0) return fromEnv.replace(/\/+$/, '');
  return 'http://localhost:8084/v1';
}

function url(path: string): string {
  return `${baseUrl()}${path.startsWith('/') ? path : `/${path}`}`;
}

async function headers(extra?: Record<string, string>): Promise<HeadersInit> {
  const base: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-Request-ID': SESSION_REQUEST_ID,
    ...(extra ?? {}),
  };
  // Attach Bearer if signed in. getAccessToken auto-refreshes within
  // the 5min expiry window; if refresh fails the session is cleared
  // and we fall through anonymous. No throw — the server's
  // attachPlayerIfPresent middleware tolerates either.
  try {
    const token = await getAccessToken();
    if (token) base.Authorization = `Bearer ${token}`;
  } catch {
    /* anonymous fallback */
  }
  return base;
}

/** A non-2xx response wraps the parsed server error body. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: Record<string, unknown>
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function parseError(resp: Response): Promise<ApiError> {
  let code = 'unknown';
  let message = resp.statusText || `HTTP ${resp.status}`;
  let details: Record<string, unknown> | undefined;
  try {
    const body = (await resp.json()) as {
      error?: { code?: string; message?: string; details?: Record<string, unknown> };
    };
    if (body.error) {
      code = body.error.code ?? code;
      message = body.error.message ?? message;
      details = body.error.details;
    }
  } catch {
    /* response wasn't JSON; leave defaults */
  }
  return new ApiError(resp.status, code, message, details);
}

/**
 * Submit a final score. Retries on 5xx / network errors; gives up
 * immediately on 4xx (no point retrying a bad payload).
 *
 * Note: server returns `score` as a string in leaderboard responses
 * (BIGINT precision). The submission body sends a JS `number`, which is
 * safe because cartridge scores fit in 53-bit float space.
 */
export async function submitScore(
  submission: ScoreSubmission
): Promise<ScoreSubmissionResponse> {
  let lastErr: unknown;
  for (let attempt = 1; attempt <= SUBMIT_RETRIES; attempt += 1) {
    try {
      const resp = await fetch(url('/scores'), {
        method: 'POST',
        headers: await headers(),
        body: JSON.stringify(submission),
      });
      if (resp.ok) {
        return (await resp.json()) as ScoreSubmissionResponse;
      }
      const err = await parseError(resp);
      // 4xx is the client's fault — retrying won't help.
      if (err.status >= 400 && err.status < 500) throw err;
      lastErr = err;
    } catch (err) {
      // Network errors (TypeError from fetch) and 5xx ApiErrors both
      // land here and get retried.
      lastErr = err;
    }
    if (attempt < SUBMIT_RETRIES) {
      const backoff = SUBMIT_BACKOFF_BASE_MS * 2 ** (attempt - 1);
      await new Promise((resolve) => setTimeout(resolve, backoff));
    }
  }
  throw lastErr instanceof Error ? lastErr : new Error('submitScore failed');
}

export interface GetLeaderboardOptions {
  period?: LeaderboardPeriod;
  limit?: number;
  cartridgeVersion?: number;
}

export async function getLeaderboard(
  cartridgeId: string,
  opts: GetLeaderboardOptions = {}
): Promise<Leaderboard> {
  const params = new URLSearchParams();
  if (opts.period) params.set('period', opts.period);
  if (opts.limit !== undefined) params.set('limit', String(opts.limit));
  if (opts.cartridgeVersion !== undefined) {
    params.set('cartridgeVersion', String(opts.cartridgeVersion));
  }
  const qs = params.toString();
  const path = `/scores/${encodeURIComponent(cartridgeId)}${qs ? `?${qs}` : ''}`;
  const resp = await fetch(url(path), { method: 'GET', headers: await headers() });
  if (!resp.ok) throw await parseError(resp);
  const body = (await resp.json()) as Leaderboard;
  // Coerce BIGINT-as-string `score` back to numbers for ease of rendering.
  return {
    ...body,
    scores: body.scores.map((entry) => ({
      ...entry,
      score: Number(entry.score),
    })),
  };
}

/**
 * Reassign anonymous scores on this device onto the signed-in player
 * (sc-3026). Caller must be signed in — the API rejects without a
 * Bearer token. Idempotent: subsequent calls return `claimed: 0`.
 *
 * Failures are non-fatal at the call site; the cabinet logs and moves
 * on. Worst case: the player's anonymous scores stay anonymous until
 * a future sign-in.
 */
export async function claimAnonymousScores(
  body: ClaimAnonymousScoresRequest
): Promise<ClaimAnonymousScoresResponse> {
  const resp = await fetch(url('/players/me/claim-anonymous-scores'), {
    method: 'POST',
    headers: await headers(),
    body: JSON.stringify(body),
  });
  if (!resp.ok) throw await parseError(resp);
  return (await resp.json()) as ClaimAnonymousScoresResponse;
}

/**
 * Fetch the deterministic daily-challenge seed for a cartridge on the
 * given UTC date. Defaults to today. Throws ApiError on 404 (unknown
 * cartridge) or network failure. Caller handles fallback to a random
 * seed if it wants daily mode to be best-effort instead of blocking.
 */
export async function getDailySeed(
  cartridgeId: string,
  date?: string
): Promise<DailySeedResponse> {
  const qs = date ? `?date=${encodeURIComponent(date)}` : '';
  const resp = await fetch(url(`/seeds/${encodeURIComponent(cartridgeId)}${qs}`), {
    method: 'GET',
    headers: await headers(),
  });
  if (!resp.ok) throw await parseError(resp);
  return (await resp.json()) as DailySeedResponse;
}

/**
 * Record one INSERT COIN. Fire-and-forget from the cabinet — a lost
 * play is a rounding error, so no retries and errors don't propagate
 * past the caller's `catch`.
 */
export async function recordPlayRemote(play: PlayRecord): Promise<void> {
  const resp = await fetch(url('/plays'), {
    method: 'POST',
    headers: await headers(),
    body: JSON.stringify(play),
  });
  if (!resp.ok) throw await parseError(resp);
}

/** Global play totals per cartridge — drives the gallery ordering. */
export async function getGlobalPlays(cartridgeVersion: number): Promise<GlobalPlays> {
  const params = new URLSearchParams({ cartridgeVersion: String(cartridgeVersion) });
  const resp = await fetch(url(`/plays?${params.toString()}`), {
    method: 'GET',
    headers: await headers(),
  });
  if (!resp.ok) throw await parseError(resp);
  return (await resp.json()) as GlobalPlays;
}
